import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Copy, Link2, Loader2 } from 'lucide-react';

interface DemoShareModalProps {
  /** Full share URL; the modal is hidden while empty. */
  url: string;
  onClose: () => void;
  /** How many workflows have been shared this browser session. */
  bundleCount?: number;
  /** Bundle URL once created (empty until the CTA is clicked). */
  bundleUrl?: string;
  bundleCreating?: boolean;
  onCreateBundle?: () => void;
}

/**
 * Shown after publishing a shareable snapshot from the public demo builder.
 * Chrome mirrors SimulatedModePromptModal. When multiple workflows have been
 * shared this session, offers collapsing them into one bundle link.
 */
export function DemoShareModal({ url, onClose, bundleCount = 0, bundleUrl = '', bundleCreating = false, onCreateBundle }: DemoShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [bundleCopied, setBundleCopied] = useState(false);

  if (!url) return null;

  const copy = async (value: string, setFlag: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(value);
      setFlag(true);
      setTimeout(() => setFlag(false), 2000);
    } catch {
      // Clipboard unavailable — the URL stays selectable in the input.
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background border rounded-lg shadow-xl max-w-lg w-full m-4"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-share-title"
      >
        <div className="px-5 py-4 border-b">
          <h3 id="demo-share-title" className="text-base font-semibold">
            Share this workflow
          </h3>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Anyone with this link can open the workflow in the demo builder — no account
            needed.
          </p>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={url}
              onFocus={e => e.target.select()}
              className="h-9 font-mono text-xs"
            />
            <Button size="sm" onClick={() => copy(url, setCopied)} className="gap-1.5 shrink-0">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>

          {bundleCount > 1 && (
            <div className="pt-3 border-t space-y-2">
              {bundleUrl ? (
                <>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    One link that opens all {bundleCount} workflows you've shared:
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={bundleUrl}
                      onFocus={e => e.target.select()}
                      className="h-9 font-mono text-xs"
                    />
                    <Button size="sm" onClick={() => copy(bundleUrl, setBundleCopied)} className="gap-1.5 shrink-0">
                      {bundleCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {bundleCopied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onCreateBundle}
                  disabled={bundleCreating}
                  className="gap-1.5"
                >
                  {bundleCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                  Get one link for all {bundleCount} workflows shared this session
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end px-5 py-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
